import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Suggestion, Suggestions } from 'designSystem/components/Suggestion'
import { Text } from 'designSystem/components/Text'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import React, { useCallback, useId, useState } from 'react'

/**
 * Follow-up questions under a DocChat answer, with shadcn's Suggestion. A
 * click asks the question.
 */
export function DocChatSuggestedQuestions({
  questions,
  onAsk,
}: {
  questions: string[]
  onAsk: (question: string) => void
}): React.ReactElement {
  const labelId = useId()

  return (
    <Box
      display='flex'
      direction='column'
      gap='xs'
      padding='xs'
      minWidth='none'
      role='group'
      aria-labelledby={labelId}>
      <Text id={labelId} variant='caption' color='muted'>
        <Trans>You can also ask</Trans>
      </Text>
      <Suggestions>
        {questions.map((question) => (
          <Suggestion key={question} suggestion={question} onClick={onAsk} />
        ))}
      </Suggestions>
    </Box>
  )
}

/**
 * The follow-up questions of the newest answer. They are not part of the
 * answer: the turn shows them under it while it is the newest one. A click
 * sends the question as the next turn with `onSuggestionSelect`, the submit
 * that Retry and the recent-search list use, and the row goes away at once.
 */
export function DocChatTurnSuggestions({
  questions,
}: {
  questions: string[]
}): React.ReactElement | null {
  const { onSuggestionSelect } = useAdvancedSearchUX()
  const [asked, setAsked] = useState(false)

  const handleAsk = useCallback(
    (question: string) => {
      setAsked(true)
      onSuggestionSelect(question, {
        clearInputAfterSubmit: true,
        startDocChatTurn: true,
      })
    },
    [onSuggestionSelect]
  )

  if (asked || questions.length === 0) return null
  return <DocChatSuggestedQuestions questions={questions} onAsk={handleAsk} />
}
